/**
 * Hostname-only navigation-referrer helpers shared by the browser drain and
 * the ingest boundary. Full referrer URLs never need to leave the page.
 */

const DOMAIN_HOST =
  /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/
const IPV4_HOST = /^(?:\d{1,3}\.){3}\d{1,3}$/
const RESERVED_LOCAL_HOST =
  /(?:^|\.)(?:localhost|local|internal|intranet|lan|home|localdomain)$|(?:^|\.)home\.arpa$/

/** Validate and normalize an already hostname-only value. */
export function normalizeReferrerHost(
  raw: string | null | undefined,
): string | undefined {
  if (!raw) return undefined
  const host = raw.toLowerCase().replace(/\.$/, "")
  if (
    host.length === 0 ||
    host.length > 128 ||
    !host.includes(".") ||
    RESERVED_LOCAL_HOST.test(host) ||
    IPV4_HOST.test(host) ||
    !DOMAIN_HOST.test(host)
  ) {
    return undefined
  }
  return host
}

/** Reduce an HTTP(S) navigation referrer URL to a validated hostname. */
export function referrerHostFromUrl(
  raw: string | null | undefined,
): string | undefined {
  if (!raw) return undefined
  try {
    const url = new URL(raw)
    if (url.protocol !== "http:" && url.protocol !== "https:") return undefined
    return normalizeReferrerHost(url.hostname)
  } catch {
    return undefined
  }
}

/** Capture the current document's navigation referrer without its path/query. */
export function currentNavigationReferrerHost(): string | undefined {
  if (typeof document === "undefined") return undefined
  return referrerHostFromUrl(document.referrer)
}
